import { EErrorMessageMode, EMapProvider, EMinimumAndroidVersion } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import {
  CREATE_GLOBAL_CONFIG,
  GlobalConfigModuleActionTypes,
  GlobalConfigModuleState,
  SET_ACTIVE_GLOBAL_CONFIGURATION,
  SET_GLOBAL_CONFIGS,
  SET_GLOBAL_CONFIGS_LOADING_STATUS
} from "./types";

const initialState: GlobalConfigModuleState = {
  isCreateGlobalConfigActive: false,
  globalConfigs: [],
  globalCommonSettings: {
    passwordPolicies: null,
    smtpSettings: null,
    googleMapsSettings: null,
    amapSettings: null,
    awsSettings: null,
    stripeSettings: null,
    iyzicoSettings: null,
    paramPaymentSettings: null,
    genericAuthSettings: null,
    oAuth2Settings: null,
    ldapSettings: null,
    googleAuth: null,
    facebookAuth: null,
    appStoreConnectApiSettings: null,
    googlePlayApiSettings: null,
    firebaseSettings: null,
    pushNotificationSettings: null,
    cameraPermissionLabelSettings: null,
    locationPermissionLabelSettings: null,
    notificationPermissionLabelSettings: null,
    bluetoothPermissionLabelSettings: null,
    commonMobileSettings: null,
    inversCloudBoxxSettings: null,
    gpsTrackingPermissionLabelSettings: null,
    gpsTrackingSettings: null,
    gpsTrackingBackgroundPermissionLabelSettings: null,
    appleSignInSettings: null,
    dbConnectionStringSettings: null,
    backendUrlSettings: null,
    frontendUrlSettings: null,
    sessionSettings: null,
    workflowEngineBaseUrlSettings: null,
    msalSettings: null,
    otpSettings: null,
    iosFirebaseSettings: null,
    notifyActionsDurationsSettings: null,
    searchServiceSettings: null,
    openAISettings: null,
    azureOpenAISettings: null,
    geminiSettings: null,
    fileUploadPathSettings: null,
    sapServiceBaseUrlSettings: null,
    googleRecaptchaAndroidSettings: null,
    googleRecaptchaIosSettings: null,
    googleRecaptchaSettings: null,
    whatsappBusinessSettings: null,
    serperToolSettings: null,
    exchangeServerMailSettings: null,
    cacheSettings: null,
    twinSettings: null,
    bucketUploadSettings: null,
    pushNotificationSettingsWeb: null,
    braveSearchSettings: null,
    slackSettings: null,
    dropboxSettings: null,
    githubSettings: null,
    googleMcpSettings: null,
    googleMapMcpSettings: null,
    amapWebServiceSettings: null,
    jiraMcpSettings: null,
    youtubeMcpSettings: null,
    trelloMcpSettings: null,
    office365McpSettings: null,
    firecrawlMcpSettings: null,
    elasticApmSettings: null,
    apifySettings: null,
    restApiConnectionSettings: null,
    ipRateLimitingSettings: null,
    documentManagementSettings: null,
    searchEngineSettings: null,
    adiRegistrationSettings: null
  },
  isGlobalConfigsLoading: false
};

export const globalConfigReducer = (
  state = initialState,
  action: GlobalConfigModuleActionTypes
): GlobalConfigModuleState => {
  switch (action.type) {
    case SET_GLOBAL_CONFIGS: {
      return {
        ...state,
        globalConfigs: action.meta.params.globalConfigs,
        globalCommonSettings: action.meta.params.globalCommonSettings
      };
    }
    case SET_ACTIVE_GLOBAL_CONFIGURATION: {
      return {
        ...state,
        activeGlobalConfiguration: action.meta.params.activeGlobalConfiguration,
        isCreateGlobalConfigActive: false
      };
    }
    case SET_GLOBAL_CONFIGS_LOADING_STATUS: {
      return {
        ...state,
        isGlobalConfigsLoading: action.meta.params.isGlobalConfigsLoading
      };
    }
    case CREATE_GLOBAL_CONFIG: {
      return {
        ...state,
        isCreateGlobalConfigActive: true,
        activeGlobalConfiguration: {
          id: Guid.create().toString(),
          name: action.meta.params.name ?? "",
          supportWeb: true,
          supportAndroid: false,
          supportIOS: false,
          supportElectron: false,
          electronConfig: null,
          isSelected: false,
          isRunConfig: false,
          configurationVersion: 1,
          publishMethods: null,
          apiConfig: {
            dbConnectionStringSettingsId: null,
            workflowDbConnectionStringSettingsId: null,
            virtualDirectory: null,
            backendUrlSettingsId: null,
            authProviderType: null,
            authProviderSettingsId: null,
            passwordPoliciesId: null,
            googleAuthId: null,
            facebookAuthId: null,
            commonMobileSettingsId: null,
            mailSettingsId: null,
            awsSettingsId: null,
            smtpSettingsId: null,
            exchangeServerMailSettingsId: null,
            customMailProviderDatasourceId: null,
            stripeSettingsId: null,
            iyzicoSettingsId: null,
            paramPaymentSettingsId: null,
            inversCloudBoxxSettingsId: null,
            googleMapsSettingsId: null,
            isTwoFactorAuthenticationEnabled: false,
            workflowMailSettingsId: null,
            workflowAwsSettingsId: null,
            workflowSmtpSettingsId: null,
            workflowEngineBaseUrlSettingsId: null,
            msalAuthId: null,
            otpSettingsId: null,
            notifyActionsDefaultDurationSettingsId: null,
            isExpireOldSessionsEnabled: false,
            errorMessageMode: EErrorMessageMode.Detailed,
            openAISettingsId: null,
            azureOpenAISettingsId: null,
            geminiSettingsId: null,
            fileUploadPathSettingsId: null,
            googleRecaptchaSettingsId: null,
            whatsappBusinessSettingsId: null,
            cacheSettingsId: null,
            bucketUploadSettingsId: null,
            braveSearchSettingsId: null,
            slackSettingsId: null,
            dropboxSettingsId: null,
            githubSettingsId: null,
            googleMcpSettingsId: null,
            googleMapMcpSettingsId: null,
            mapProvider: EMapProvider.GoogleMaps,
            amapWebServiceSettingsId: null,
            jiraMcpSettingsId: null,
            youtubeMcpSettingsId: null,
            trelloMcpSettingsId: null,
            office365McpSettingsId: null,
            firecrawlMcpSettingsId: null,
            elasticApmSettingsId: null,
            apifySettingsId: null,
            restApiConnectionSettingsId: null,
            restApiDatasourceBindings: null,
            ipRateLimitingSettingsId: null,
            documentManagementSettingsId: null,
            searchEngineSettingsId: null,
            searchServiceSettingsId: null,
            sapServiceBaseUrlSettingsId: null
          },
          webConfig: {
            serverBaseUrl: "",
            appIcon: null,
            title: null,
            frontendUrlSettingsId: null,
            sessionSettingsId: null,
            googleMapsSettingsId: null,
            mapProvider: EMapProvider.GoogleMaps,
            amapSettingsId: null,
            allowEndUserMapProviderChoice: false,
            dateFormat: null,
            timeFormat: null,
            pushNotificationSettingsId: null,
            metaDescription: null,
            enablePwa: false
          },
          androidConfig: {
            serverUrl: "",
            appName: "",
            appBundleId: "",
            iconFilePath: "",
            iconRoundedFileName: "",
            splashScreenImageName: "",
            splashScreenBackcolor: "E1E5EB",
            targetAndroidVersion: "",
            appVersion: "1.0.0",
            versionCode: 1,
            minimumAndroidVersion: EMinimumAndroidVersion.Android9,
            useClearTextTraffic: true,
            trustAllCerts: true,
            cameraPermissionLabelSettingsId: null,
            locationPermissionLabelSettingsId: null,
            notificationPermissionLabelSettingsId: null,
            bluetoothPermissionLabelSettingsId: null,
            firebaseSettingsId: null,
            googleMapsSettingsId: null,
            mapProvider: EMapProvider.GoogleMaps,
            amapSettingsId: null,
            allowEndUserMapProviderChoice: false,
            googlePlayApiSettingsId: null,
            gpsTrackingPermissionLabelSettingsId: null,
            gpsTrackingBackgroundPermissionLabelSettingsId: null,
            gpsTrackingSettingsId: null,
            deviceType: 0,
            orientation: "any",
            imageFit: "default",
            isHapticEnabled: true,
            isSecureScreenModeEnabled: false,
            googleRecaptchaAndroidSettingsId: null,
            adiRegistrationSettingsId: null
          },
          iosConfig: {
            serverUrl: "",
            appName: "",
            appBundleId: "",
            iconFileName: "",
            splashScreenImageName: "",
            splashScreenBackcolor: "E1E5EB",
            appVersion: "1.0.0",
            appleSigninSettingsId: null,
            appStoreConnectApiSettingsId: null,
            cameraPermissionLabelSettingsId: null,
            locationPermissionLabelSettingsId: null,
            notificationPermissionLabelSettingsId: null,
            bluetoothPermissionLabelSettingsId: null,
            pushNotificationSettingsId: null,
            gpsTrackingBackgroundPermissionLabelSettingsId: null,
            gpsTrackingPermissionLabelSettingsId: null,
            gpsTrackingSettingsId: null,
            firebaseSettingsId: null,
            deviceType: 0,
            orientation: "any",
            imageFit: "default",
            isHapticEnabled: true,
            googleRecaptchaIosSettingsId: null
          }
        }
      };
    }
    default:
      return state;
  }
};
